import type * as THREE from 'three';
import { FLAGS } from '@/flags';

/** Chromium 才有 performance.memory，非标准 API，拿不到时该指标留空 */
interface IPerformanceWithMemory extends Performance {
  memory?: { usedJSHeapSize: number };
}

interface IPhaseRecord {
  阶段: string;
  耗时: string;
}

const getHeapUsed = (): number | undefined => (performance as IPerformanceWithMemory).memory?.usedJSHeapSize;

const ms = (value: number) => `${Math.round(value * 100) / 100}ms`;

const kb = (bytes: number) => `${Math.round((bytes / 1024) * 10) / 10}KB`;

/**
 * 截图性能探测：分阶段耗时 + 单帧绘制量 + GPU 对象增量 + JS 堆增量 + 输出体积。
 *
 * 浏览器里能测到的硬件占用只有这些间接指标：GPU 执行时间需要
 * EXT_disjoint_timer_query_webgl2，出于安全考虑主流浏览器早已不再提供，所以不做。
 *
 * 各阶段耗时都是墙上时间。除「编码」以外的阶段是一个同步块，
 * 「编码」是 toBlob 的异步回调（含一次任务队列跳转），不阻塞主线程，
 * 所以主线程阻塞时长是「总耗时减去编码」。
 */
export class CaptureProfiler {
  private readonly _label: string;
  private readonly _renderer?: THREE.WebGLRenderer;
  private readonly _enabled: boolean;
  private readonly _startTime: number;
  private readonly _heapBefore?: number;
  private readonly _memoryBefore?: { geometries: number; textures: number };

  private _lastTime: number;
  private _phases: IPhaseRecord[] = [];
  private _meta: Record<string, string | number> = {};

  constructor(label: string, renderer?: THREE.WebGLRenderer) {
    this._label = label;
    this._renderer = renderer;
    this._enabled = FLAGS.FEATURES_DEV.ENABLE_CAPTURE_PERF;
    this._startTime = performance.now();
    this._lastTime = this._startTime;

    if (!this._enabled) return;

    this._heapBefore = getHeapUsed();
    if (renderer) {
      this._memoryBefore = { geometries: renderer.info.memory.geometries, textures: renderer.info.memory.textures };
    }
  }

  /** 记录「从上一次 step（或开始）到现在」这一段的耗时 */
  step(name: string) {
    if (!this._enabled) return;

    const now = performance.now();
    this._phases.push({ 阶段: name, 耗时: ms(now - this._lastTime) });
    this._lastTime = now;
  }

  /** 往汇总里挂一条附加信息，供各阶段随手补充上下文（如渲染尺寸） */
  meta(key: string, value: string | number) {
    if (!this._enabled) return;
    this._meta[key] = value;
  }

  /** 输出汇总。blob 为最终产物，用于统计体积 */
  end(blob: Blob | null) {
    if (!this._enabled) return;

    const total = performance.now() - this._startTime;
    const summary: Record<string, string | number> = { 总耗时: ms(total) };

    // 体积和格式一起打：对比不同编码时不用猜这张图到底是哪种格式（WebP 还可能回落成 PNG）
    if (blob) {
      summary['输出体积'] = kb(blob.size);
      summary['输出格式'] = blob.type;
    }

    if (this._renderer) {
      const { render, memory } = this._renderer.info;
      summary['drawCall'] = render.calls;
      summary['三角面'] = render.triangles;
      if (this._memoryBefore) {
        summary['新增 geometry'] = memory.geometries - this._memoryBefore.geometries;
        summary['新增 texture'] = memory.textures - this._memoryBefore.textures;
      }
    }

    const heapAfter = getHeapUsed();
    if (this._heapBefore !== undefined && heapAfter !== undefined) {
      summary['JS 堆增量'] = kb(heapAfter - this._heapBefore);
    }

    Object.assign(summary, this._meta);

    console.groupCollapsed(`[${this._label}] ${ms(total)}`);
    if (this._phases.length) console.table(this._phases);
    console.table(summary);
    console.groupEnd();
  }
}

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => resolve('');
    reader.readAsDataURL(blob);
  });

const decodeImageSize = (url: string) =>
  new Promise<{ width: number; height: number }>((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve({ width: 0, height: 0 });
    image.src = url;
  });

/** 控制台里预览图的长边上限，原尺寸太占地方 */
const CONSOLE_IMAGE_MAX_EDGE = 240;

/**
 * 把截图结果直接画进控制台，便于肉眼确认画面质量。
 * 等比缩到 CONSOLE_IMAGE_MAX_EDGE 以内，白底正好和缩放后的图等大，不会有留白让人误判比例；
 * 真实像素尺寸打在标签行上。
 *
 * 必须转成 data URL：`%c` 的样式是在 devtools:// 前端文档里渲染的，
 * 而 object URL 绑定在页面 origin 上，那边取不到，图会是空白的。
 * 为了看图多编一次 base64，只发生在开了开关的开发环境，上传路径仍然走二进制。
 */
export const logCaptureImage = async (label: string, blob: Blob | null) => {
  if (!FLAGS.FEATURES_DEV.ENABLE_CAPTURE_PERF || !blob) return;

  const url = await blobToDataUrl(blob);
  if (!url) return;

  const { width, height } = await decodeImageSize(url);
  if (!width || !height) return;

  const scale = Math.min(1, CONSOLE_IMAGE_MAX_EDGE / Math.max(width, height));
  const boxWidth = Math.round(width * scale);
  const boxHeight = Math.round(height * scale);

  // 标签单独占一行，否则文字会把图片的盒子撑宽
  console.log(
    `%s ${width}×${height}\n%c `,
    label,
    [
      `padding: ${boxHeight / 2}px ${boxWidth / 2}px`,
      // font-size 必须为 0：1px 的字也会带来约 1.5px 的行内内容高度，盒子就撑出了额外的高
      'font-size: 0',
      'line-height: 0',
      `background: #fff url(${url}) no-repeat`,
      `background-size: ${boxWidth}px ${boxHeight}px`
    ].join(';')
  );
};
